"use client";

import { AuthButtons, Button, WishlistCard, WishlistItemHorizontal } from "@/components";
import AuthDialog from "@/components/AuthDialog/AuthDialog";
import { useAuth } from "@/contexts/AuthContext";
import { useDialog } from "@/contexts/DialogContext";
import { useFavorites } from "@/contexts/FavoritesContext";
import type { Listing } from "@/services/api";
import { FORM_TYPES } from "@/utils";
import { WISHLIST_STRINGS } from "./wishlistStrings";

interface WishlistContentProps {
    isLargeScreen: boolean;
    filteredWishlistItems: Listing[];
    wishlistListings: Listing[];
    onOpenSortBy: () => void;
    onOpenFilterBy: () => void;
}

const SKELETON_ITEM_COUNT = 5;

const ContentContainer = ({ children }: { children: React.ReactNode }) => (
    <div className="px-4 overflow-visible">{children}</div>
);

const MessageContainer = ({ children }: { children: React.ReactNode }) => (
    <div className="mb-8 lg:mt-6">{children}</div>
);

const EmptyMessageContainer = ({ children }: { children: React.ReactNode }) => (
    <div className="lg:mt-6">
        <div className="mb-8 pt-8 md:pt-12">{children}</div>
    </div>
);

const HorizontalSkeletonItem = () => (
    <div className="bg-white pb-8 mb-8 overflow-visible animate-pulse">
        <div className="flex gap-6 h-[340px] bg-white overflow-visible">
            <div className="w-80 flex-shrink-0 relative bg-white">
                <div className="aspect-square bg-light-grey rounded" />
            </div>

            <div className="flex-1 min-w-0 flex flex-col overflow-visible">
                <div className="flex gap-6 mb-6">
                    <div className="flex-1 min-w-0">
                        <div className="h-6 bg-light-grey rounded mb-2 w-3/4" />
                        <div className="h-5 bg-light-grey rounded mb-4 w-2/3" />
                        <div className="h-6 bg-light-grey rounded mb-2 w-24" />
                        <div className="h-4 bg-light-grey rounded mb-4 w-1/2" />
                        <div className="h-4 bg-light-grey rounded w-20" />
                    </div>

                    <div className="flex-shrink-0">
                        <div className="h-12 w-48 bg-light-grey rounded border" />
                    </div>
                </div>

                <div className="flex-1 flex items-end relative xl:-mr-12 lg:-mr-10">
                    <div className="w-full h-[120px] bg-light-grey rounded" />
                </div>
            </div>
        </div>
    </div>
);

const GridSkeletonItem = () => (
    <div className="animate-pulse">
        <div className="aspect-square bg-light-grey rounded mb-4" />
        <div className="p-4">
            <div className="mb-4">
                <div className="h-4 bg-light-grey rounded mb-2 w-3/4" />
                <div className="h-4 bg-light-grey rounded mb-2 w-1/2" />
            </div>
            <div className="h-4 bg-light-grey rounded mb-2 w-1/3" />
            <div className="h-3 bg-light-grey rounded w-1/4" />
        </div>
        <div className="px-4 pb-4">
            <div className="h-10 bg-light-grey rounded border" />
        </div>
    </div>
);

const LoadingSkeleton = () => (
    <ContentContainer>
        <div className="grid grid-cols-1 sm:hidden gap-2">
            {Array.from({ length: 8 }, (_, index) => (
                <GridSkeletonItem key={index} />
            ))}
        </div>

        <div className="hidden sm:block space-y-4 overflow-visible">
            {Array.from({ length: SKELETON_ITEM_COUNT }, (_, index) => (
                <HorizontalSkeletonItem key={index} />
            ))}
        </div>
    </ContentContainer>
);

interface FilterControlsProps {
    onOpenSortBy: () => void;
    onOpenFilterBy: () => void;
}

const FilterControls = ({ onOpenSortBy, onOpenFilterBy }: FilterControlsProps) => (
    <div className="px-4 xl:px-0 mb-6">
        <div className="hidden md:flex gap-2 xl:hidden w-full">
            <Button
                onClick={onOpenSortBy}
                className="btn-raleway flex-1 flex items-center justify-center h-11 sm:h-12 px-3 sm:px-4 py-2.5 sm:py-3 border border-black bg-white text-black hover:bg-light-grey"
                aria-label="Open sort options"
            >
                SORT BY
            </Button>
            <Button
                onClick={onOpenFilterBy}
                className="btn-raleway flex-1 flex items-center justify-center h-11 sm:h-12 px-3 sm:px-4 py-2.5 sm:py-3 border border-black bg-white text-black hover:bg-light-grey"
                aria-label="Open filter options"
            >
                FILTER BY
            </Button>
        </div>
    </div>
);

interface UnauthenticatedContentProps {
    onLoginClick: () => void;
    onRegisterClick: () => void;
}

const UnauthenticatedContent = ({
    onLoginClick,
    onRegisterClick
}: UnauthenticatedContentProps) => (
    <ContentContainer>
        <MessageContainer>
            <div className="text-left px-2 md:px-0 lg:px-0">
                <p className="text-lg text-black font-raleway mb-4 leading-relaxed">
                    {WISHLIST_STRINGS.LOGGED_OUT_MESSAGE.FIRST_PART}
                </p>
                <p className="text-lg text-black font-raleway mb-6">
                    {WISHLIST_STRINGS.LOGGED_OUT_MESSAGE.SECOND_PART}
                </p>
                <AuthButtons
                    onLoginClick={onLoginClick}
                    onRegisterClick={onRegisterClick}
                />
            </div>
        </MessageContainer>
    </ContentContainer>
);

const EmptyWishlistContent = () => (
    <ContentContainer>
        <EmptyMessageContainer>
            <div className="text-left px-2 md:px-0">
                <p className="text-lg text-black font-raleway mb-8 md:mb-6 leading-relaxed">
                    {WISHLIST_STRINGS.EMPTY_WISHLIST_MESSAGE}
                </p>
                <Button
                    href="/listings"
                    className="btn-raleway bg-white text-black hover:bg-light-grey py-4 px-8 md:py-4 md:px-6 border border-black !min-w-0 !w-auto"
                    aria-label="Browse all bags"
                >
                    VIEW ALL BAGS
                </Button>
            </div>
        </EmptyMessageContainer>
    </ContentContainer>
);

interface WishlistItemsContentProps {
    items: Listing[];
}

const WishlistItemsContent = ({ items }: WishlistItemsContentProps) => (
    <ContentContainer>
        <div className="grid grid-cols-1 gap-2 sm:hidden" role="list" aria-label="Wishlist items">
            {items.map((listing) => (
                <div key={listing._id} role="listitem">
                    <WishlistCard listing={listing} showDealLabel={true} />
                </div>
            ))}
        </div>

        <div className="hidden sm:block space-y-4 overflow-visible" role="list" aria-label="Wishlist items">
            {items.map((listing) => (
                <div key={listing._id} role="listitem">
                    <WishlistItemHorizontal listing={listing} showDealLabel={true} />
                </div>
            ))}
        </div>
    </ContentContainer>
);

const WishlistContent = ({
    filteredWishlistItems,
    onOpenSortBy,
    onOpenFilterBy
}: WishlistContentProps) => {
    const { isAuthenticated, isLoading } = useAuth();
    const { loading: isWishlistFetching } = useFavorites();
    const { openDialog } = useDialog();

    const openAuthDialog = (mode: typeof FORM_TYPES[keyof typeof FORM_TYPES]) => {
        openDialog({
            component: AuthDialog,
            props: { initialMode: mode },
            options: {
                closeOnOverlayClick: true,
                closeOnEscape: true,
                showCloseButton: true,
            },
        });
    };

    if (isLoading || isWishlistFetching) {
        return (
            <section className="pb-4 sm:pb-25">
                <LoadingSkeleton />
            </section>
        );
    }

    if (!isAuthenticated) {
        return (
            <section className="pb-4 sm:pb-25">
                <UnauthenticatedContent
                    onLoginClick={() => openAuthDialog(FORM_TYPES.LOGIN)}
                    onRegisterClick={() => openAuthDialog(FORM_TYPES.REGISTER)}
                />
            </section>
        );
    }

    const hasWishlistItems = filteredWishlistItems.length > 0;

    return (
        <section className="pb-4" role="main" aria-label="Wishlist content">
            {hasWishlistItems && (
                <FilterControls
                    onOpenSortBy={onOpenSortBy}
                    onOpenFilterBy={onOpenFilterBy}
                />
            )}
            {hasWishlistItems ? (
                <WishlistItemsContent items={filteredWishlistItems} />
            ) : (
                <EmptyWishlistContent />
            )}
        </section>
    );
};

export default WishlistContent;